import { Dropdown } from '@/shared/common/dropdown/dropdown';

export class BreadcrumbsDropdown {
	private readonly element: HTMLElement;
	private dropdown: Dropdown | null = null;
	private trigger: HTMLElement | null = null;

	constructor(element: HTMLElement) {
		this.element = element;

		if (!this.element) return;

		this.init();
	}

	public init(): void {
		this.trigger = this.element.querySelector<HTMLElement>('.breadcrumbs__trigger');

		this.dropdown = new Dropdown(this.element, {
			selectorButton: '.breadcrumbs__trigger',
			selectorContent: '.breadcrumbs__menu',
		});

		this.element.addEventListener('mouseenter', this.onMouseEnter);
		this.element.addEventListener('mouseleave', this.onMouseLeave);
		this.trigger?.addEventListener('click', this.stopTriggerClickCapture, true);
	}

	private stopTriggerClickCapture = (event: MouseEvent): void => {
		event.stopImmediatePropagation();
	};

	private onMouseEnter = (): void => {
		if (!this.dropdown) return;
		this.dropdown.open();
		requestAnimationFrame(() => this.positionMenu());
	};

	private onMouseLeave = (): void => {
		if (!this.dropdown) return;

		const menu = this.element.querySelector<HTMLElement>('.breadcrumbs__menu');
		if (menu) this.scheduleMenuShiftReset(menu);

		this.dropdown.close();
	};

	private scheduleMenuShiftReset(menu: HTMLElement): void {
		const resetIfStillClosed = () => {
			if (!this.dropdown) return;
			if (this.element.classList.contains('dropdown--active')) return;
			this.resetMenuPosition();
		};

		const onTransitionEnd = (event: TransitionEvent): void => {
			if (event.propertyName !== 'opacity') return;
			menu.removeEventListener('transitionend', onTransitionEnd);
			resetIfStillClosed();
		};

		menu.addEventListener('transitionend', onTransitionEnd);
	}

	private positionMenu(): void {
		const menu = this.element.querySelector<HTMLElement>('.breadcrumbs__menu');

		if (!menu) return;

		this.resetMenuPosition();

		menu.style.right = 'auto';
		menu.style.left = '0';

		const viewportPadding = 20;
		const viewportWidth = document.documentElement.clientWidth;
		const dropdownRect = this.element.getBoundingClientRect();
		let menuRect = menu.getBoundingClientRect();

		if (menuRect.right > viewportWidth - viewportPadding) {
			const shift = menuRect.right - (viewportWidth - viewportPadding);
			menu.style.left = `${-shift}px`;
			menuRect = menu.getBoundingClientRect();
		}

		if (menuRect.left < viewportPadding) {
			menu.style.left = `${viewportPadding - dropdownRect.left}px`;
		}
	}

	private resetMenuPosition(): void {
		const menu = this.element.querySelector<HTMLElement>('.breadcrumbs__menu');

		if (!menu) return;

		menu.style.left = '';
		menu.style.right = '';
	}

	public destroy(): void {
		if (!this.dropdown) return;

		this.element.removeEventListener('mouseenter', this.onMouseEnter);
		this.element.removeEventListener('mouseleave', this.onMouseLeave);
		this.trigger?.removeEventListener('click', this.stopTriggerClickCapture, true);
		this.dropdown.destroy();
		this.dropdown = null;
	}
}
